import React from 'react';
import { motion } from 'framer-motion';
import { FileText, BarChart3, Shield, Eye, MessageSquare, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const reports = [
  { title: 'Annual Report 2024', year: '2024', type: 'Annual Report' },
  { title: 'Annual Report 2023', year: '2023', type: 'Annual Report' },
  { title: 'Annual Report 2022', year: '2022', type: 'Annual Report' },
  { title: 'Annual Report 2021', year: '2021', type: 'Annual Report' }
];

const financials = [
  { title: 'Audited Financial Statements 2024', year: '2024' },
  { title: 'Audited Financial Statements 2023', year: '2023' },
  { title: 'Audited Financial Statements 2022', year: '2022' }
];

const policies = [
  { title: 'Code of Conduct', description: 'Guidelines for ethical behavior and professional standards.' },
  { title: 'Anti-Corruption Policy', description: 'Zero tolerance approach to corruption and fraud.' },
  { title: 'Safeguarding Policy', description: 'Protection of vulnerable populations we serve.' },
  { title: 'Environmental Policy', description: 'Our commitment to environmental sustainability.' },
  { title: 'Data Protection Policy', description: 'How we protect personal information.' }
];

export default function TransparencyPage() {
  return (
    <div data-testid="transparency-page">
      {/* Hero Section */}
      <section className="bg-slate-50 section-padding">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold font-['Manrope'] text-slate-900 mb-6">
                Transparency
              </h1>
              <p className="text-lg text-slate-600">
                We are committed to operating with full transparency and accountability. Explore our reports, financial statements, and policies.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              data-testid="commitment-accountability"
            >
              <div className="w-16 h-16 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-[#7BC4E2]" />
              </div>
              <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-2">Accountability</h3>
              <p className="text-slate-600">We hold ourselves to the highest standards of accountability in all our operations.</p>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              data-testid="commitment-openness"
            >
              <div className="w-16 h-16 bg-[#E08B7A]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Eye className="w-8 h-8 text-[#E08B7A]" />
              </div>
              <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-2">Openness</h3>
              <p className="text-slate-600">Our financial records and organizational data are publicly available.</p>
            </motion.div>
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              data-testid="commitment-integrity"
            >
              <div className="w-16 h-16 bg-[#9DB9A3]/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-[#9DB9A3]" />
              </div>
              <h3 className="text-xl font-bold font-['Manrope'] text-slate-900 mb-2">Integrity</h3>
              <p className="text-slate-600">We maintain ethical standards and welcome stakeholder feedback.</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Annual Reports Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <FileText className="w-8 h-8 text-[#7BC4E2]" />
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                Annual Reports
              </h2>
            </div>

            <div className="space-y-4">
              {reports.map((report, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-6 rounded-2xl flex items-center justify-between card-lift"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`report-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#7BC4E2]/10 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-[#7BC4E2]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{report.title}</h3>
                      <p className="text-sm text-slate-500">{report.type}</p>
                    </div>
                  </div>
                  <Button variant="outline" className="rounded-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Financial Statements Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <BarChart3 className="w-8 h-8 text-[#E08B7A]" />
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                Financial Statements
              </h2>
            </div>

            <div className="space-y-4">
              {financials.map((statement, index) => (
                <motion.div
                  key={index}
                  className="bg-slate-50 p-6 rounded-2xl flex items-center justify-between card-lift"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  data-testid={`financial-${index}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-[#E08B7A]/10 rounded-xl flex items-center justify-center">
                      <BarChart3 className="w-6 h-6 text-[#E08B7A]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">{statement.title}</h3>
                      <p className="text-sm text-slate-500">Audited by independent auditors</p>
                    </div>
                  </div>
                  <Button variant="outline" className="rounded-full">
                    <Download className="w-4 h-4 mr-2" />
                    Download
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Policies Section */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Shield className="w-8 h-8 text-[#9DB9A3]" />
              <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900">
                Policies & Codes
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4" data-testid="policies-accordion">
              {policies.map((policy, index) => (
                <AccordionItem 
                  key={index} 
                  value={`policy-${index}`}
                  className="bg-white rounded-2xl border-none shadow-sm"
                  data-testid={`policy-${index}`}
                >
                  <AccordionTrigger className="px-6 py-4 hover:no-underline">
                    <span className="font-semibold text-slate-900">{policy.title}</span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4">
                    <p className="text-slate-600 mb-4">{policy.description}</p>
                    <Button variant="outline" size="sm" className="rounded-full">
                      <Download className="w-4 h-4 mr-2" />
                      Download Policy
                    </Button>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Complaints Mechanism */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <MessageSquare className="w-12 h-12 text-[#E5B75F] mx-auto mb-4" />
            <h2 className="text-2xl md:text-3xl font-bold font-['Manrope'] text-slate-900 mb-4">
              Accountability & Complaints Mechanism
            </h2>
            <p className="text-slate-600 mb-6">
              We welcome feedback from all stakeholders. If you have concerns about our work or wish to report any issues, please contact us through our secure complaints mechanism.
            </p>
            <Button className="btn-primary" data-testid="submit-complaint-button">
              Submit Feedback or Complaint
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
